import type { Message, Part } from "@opencode-ai/sdk/v2/client"
import type { ServerEvent } from "@/context/server-sdk"
import type { CoworkSession, CoworkWorkflowPipelineData } from "./api"

export const resultTabs = ["files", "extractions", "transformations", "output", "report", "approval"] as const
export type ResultTab = typeof resultTabs[number]
export type WorkflowScope = { companyID: string; projectID: string; periodID?: string | null; remoteSessionID?: string }
export type SessionStatus = "idle" | "busy" | "retry"
export type WorkflowTabState = { complete: Partial<Record<ResultTab, boolean>>; active: ResultTab[] }

export function workflowRootIDs(sessions: CoworkSession[], scope: WorkflowScope) {
  return sessions.filter((session) => session.company_id === scope.companyID && session.project_id === scope.projectID &&
    (session.period_id ?? null) === (scope.periodID ?? null) && session.status !== "deleted")
    .flatMap((session) => session.remote_opencode_session_id ? [session.remote_opencode_session_id] : [])
}

export function belongsToWorkflow(id: string, roots: readonly string[], parents: Record<string, { parentID?: string } | undefined>) {
  const visited = new Set<string>()
  let current: string | undefined = id
  while (current && !visited.has(current)) {
    if (roots.includes(current)) return true
    visited.add(current)
    current = parents[current]?.parentID
  }
  return false
}

export function workflowStatusEvent(event: ServerEvent): { sessionID: string; status: SessionStatus } | undefined {
  const current = event.current
  if (current?.type === "session.execution.started") return { sessionID: current.data.sessionID, status: "busy" }
  if (current?.type === "session.retry.scheduled") return { sessionID: current.data.sessionID, status: "retry" }
  if (current && ["session.execution.succeeded", "session.execution.failed", "session.execution.interrupted"].includes(current.type) && "sessionID" in current.data) {
    return { sessionID: current.data.sessionID as string, status: "idle" }
  }
  if (event.type === "session.status") return { sessionID: event.properties.sessionID, status: event.properties.status.type }
}

const operations = {
  "get-context": "preparing", "list-files": "reviewing", "get-file": "reviewing", "get-file-content": "reviewing",
  "save-extraction": "savingExtraction", "list-extractions": "analysing", "save-transformation": "savingOutputs",
  "list-transformations": "analysing", "save-artifact": "savingReport", "list-artifacts": "validating",
  "save-dashboard": "savingDashboard", "get-dashboard": "buildingDashboard",
  "get-accounting-context": "reviewing", "get-accounting-readiness": "validating",
  "create-tax-report": "generatingReport", "create-export-package": "savingReport",
} as const
export type WorkflowStage = typeof operations[keyof typeof operations] | "extracting" | "generatingReport" | "coordinating" | "thinking" | "responding"

export function workflowToolStage(tool: string, input: Record<string, unknown>): WorkflowStage | undefined {
  const name = tool.replaceAll("_", "-").toLowerCase()
  if (Object.hasOwn(operations, name)) return operations[name as keyof typeof operations]
  if (name === "run-generated-extractor") return "extracting"
  if (name === "execute-definition-fixture") return "validating"
  if (name === "skill" || name === "todowrite") return "preparing"
  if (["read", "glob", "grep", "list", "webfetch", "websearch"].includes(name)) return "reviewing"
  if (["write", "edit", "apply-patch"].includes(name)) return "preparing"
  if (name === "task") return "coordinating"
  if (!["bash", "shell", "python"].includes(name)) return
  // Classify only the executed command, never instructions or output text.
  const command = typeof input.command === "string" ? input.command.trim() : ""
  const invocation = command.match(/^(?:python(?:3(?:\.\d+)?)?|bun|node)\s+["']?([^\s"']+)["']?(?:\s+([a-z-]+))?/)
  const script = invocation?.[1].split("/").at(-1)
  const operation = invocation?.[2]
  if (script === "cowork_agent_api.py" && operation && Object.hasOwn(operations, operation)) return operations[operation as keyof typeof operations]
  if (script === "render_factsheet.py") return "generatingReport"
  if (script === "run-generated-extractor.ts") return "extracting"
  if (script === "execute-definition-fixture.ts") return "validating"
  return "analysing"
}

export function tabsForStage(stage: string): ResultTab[] {
  if (stage === "reviewing") return ["files"]
  if (["extracting", "savingExtraction"].includes(stage)) return ["extractions"]
  if (["analysing", "savingOutputs"].includes(stage)) return ["transformations", "output"]
  if (["generatingReport", "savingReport"].includes(stage)) return ["report"]
  return []
}

export function workflowSessionActivity(messages: Message[], parts: Record<string, Part[]>) {
  const message = messages.at(-1)
  if (!message || message.role !== "assistant" || message.time.completed) return
  const current = parts[message.id] ?? []
  const tools = current.flatMap((part) => {
    if (part.type !== "tool" || !["running", "pending"].includes(part.state.status)) return []
    const stage = workflowToolStage(part.tool, part.state.input)
    return stage ? [{ stage, priority: stage === "coordinating" ? 1 : 2, time: part.state.status === "running" ? part.state.time.start : message.time.created }] : []
  }).sort((a, b) => b.priority - a.priority || b.time - a.time)
  if (tools.length) return tools[0]
  if (current.some((part) => part.type === "reasoning" && !part.time.end)) return { stage: "thinking" as const, priority: 0, time: message.time.created }
  if (current.some((part) => part.type === "text" && !part.synthetic && !part.time?.end)) return { stage: "responding" as const, priority: 0, time: message.time.created }
}

export function savedWorkflowResult(event: ServerEvent, parts: Record<string, Part[]>) {
  const current = event.current
  const part = current?.type === "session.tool.success"
    ? parts[current.data.assistantMessageID]?.find((part) => part.type === "tool" && part.callID === current.data.callID)
    : event.type === "message.part.updated" ? event.properties.part : undefined
  if (part?.type !== "tool" || part.state.status !== "completed") return
  const stage = workflowToolStage(part.tool, part.state.input)
  if (!stage || !["savingExtraction", "savingOutputs", "savingReport", "savingDashboard"].includes(stage)) return
  return { sessionID: part.sessionID, key: `${part.sessionID}:${part.callID}:${part.state.time.end}`, tabs: tabsForStage(stage) }
}

export function workflowCompletion(data?: CoworkWorkflowPipelineData): WorkflowTabState["complete"] {
  if (!data) return { files: false, extractions: false, transformations: false, output: false, report: false, approval: false }
  const scoped = (item: { company_id?: string; project_id: string }) => (!item.company_id || item.company_id === data.company_id) && item.project_id === data.project_id
  const files = data.files.filter(scoped)
  const transformations = data.transformations.filter((item) => scoped(item) && item.status === "completed")
  const reports = data.artifacts.filter((item) => scoped(item) && item.artifact_type === "report" && item.artifact_metadata.source !== "dashboard_share" &&
    ["created", "completed", "generated", "published", "approved", "draft"].includes(item.status))
  const approvals = data.approvals.filter((item) => reports.some((report) => report.id === item.report_artifact_id))
  return {
    files: files.length > 0 && files.every((file) => file.status === "uploaded"),
    extractions: data.extractions.some((item) => scoped(item) && item.status === "completed"),
    transformations: transformations.length > 0,
    output: transformations.some((item) => {
      const tables = item.output_json.output_tables ?? (!item.output_json.pipeline_run_id ? item.output_json.tables : undefined)
      const values = Array.isArray(tables) ? tables : tables && typeof tables === "object" ? Object.values(tables) : []
      return values.some((table) => table && typeof table === "object" && table.visibility !== "internal" && (Array.isArray(table.rows) || Array.isArray(table.columns) || Array.isArray(table.headers)))
    }),
    report: reports.length > 0,
    approval: approvals.length > 0 && approvals.every((item) => item.status === "approved"),
  }
}
